import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { parseEther, type Address } from 'viem';
import { useConnection } from 'wagmi';
import { ActionButton } from '../tx/ActionButton.tsx';
import { Button } from '../ui/primitives.tsx';
import { useToast } from '../ui/toasts.tsx';
import { CHAIN_ID, DEPLOYMENT as D, EXPLORER, FAUCET_URL } from '../../config/env.ts';
import { useI18n, type MessageKey } from '../../i18n/index.tsx';
import { fmtDuration } from '../../lib/format.ts';
import { refreshAfter } from '../tx/refresh.ts';

/** The testnet USDT stand-in (script/local/FaucetToken.sol): anyone may mint up to 100k per call. */
const faucetAbi = [
  {
    type: 'function',
    name: 'mint',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [],
  },
] as const;

const TBNB_FAUCET = 'https://www.bnbchain.org/en/testnet-faucet';

/** The answer of services/faucet `POST /drip`. */
interface DripReply {
  bnb?: { hash: string } | { skipped: 'has-enough' | 'faucet-low' };
  usdtHash?: string;
  error?: 'bad-address' | 'cooldown' | 'ip-limit' | 'day-limit' | 'failed';
  retryAfterSec?: number;
}

/** What to tell the tester about a faucet reply: [tone, message key, vars]. Exported for tests. */
export function dripOutcome(status: number, r: DripReply): ['ok' | 'error', MessageKey, Record<string, string>?] {
  if (status === 200 && r.usdtHash) {
    if (r.bnb && 'hash' in r.bnb) return ['ok', 'testnet.sent'];
    return ['ok', r.bnb?.skipped === 'faucet-low' ? 'testnet.sentNoBnb' : 'testnet.sentUsdt'];
  }
  const time = { time: fmtDuration(r.retryAfterSec ?? 60) };
  if (r.error === 'cooldown') return ['error', 'testnet.cooldown', time];
  if (r.error === 'ip-limit' || r.error === 'day-limit') return ['error', 'testnet.limit', time];
  return ['error', 'testnet.failed'];
}

/**
 * One press, no gas: the server sends test BNB (only to a wallet without enough for fees) and 1,000 test USDT.
 * A new tester has no BNB, so a mint they sign themselves could never pay its own fee.
 */
function FaucetButton({ url, address }: { url: string; address: Address }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const press = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${url}/drip`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ address }),
      });
      const reply = (await res.json().catch(() => ({}))) as DripReply;
      const [tone, key, vars] = dripOutcome(res.status, reply);
      toast(
        tone,
        <span>
          {t(key, vars)}
          {tone === 'ok' && EXPLORER && reply.usdtHash ? (
            <>
              {' · '}
              <a href={`${EXPLORER}/tx/${reply.usdtHash}`} target="_blank" rel="noreferrer">
                {t('common.viewTx')}
              </a>
            </>
          ) : null}
        </span>,
      );
      if (tone === 'ok') await refreshAfter(() => qc.invalidateQueries()); // the server waited for its receipts
    } catch {
      toast('error', t('testnet.failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button block variant="secondary" disabled={busy} onClick={press} data-testid="faucet-drip">
      {busy ? t('testnet.sending') : t('testnet.mint')}
    </Button>
  );
}

/** BSC testnet only: says the money is test money and hands out what a tester needs (test BNB, test USDT). */
export function TestnetBar() {
  const { t } = useI18n();
  const { address } = useConnection();
  if (CHAIN_ID !== 97) return null;
  return (
    <div className="testnet-bar" data-testid="testnet-bar">
      <span className="tn-pill">{t('testnet.pill')}</span>
      <span className="tn-text">
        {t('testnet.text')}{' '}
        <a href={TBNB_FAUCET} target="_blank" rel="noreferrer">
          {t('testnet.faucet')}
        </a>
      </span>
      {address ? (
        <span className="tn-action">
          {FAUCET_URL ? (
            <FaucetButton url={FAUCET_URL} address={address} />
          ) : (
            <ActionButton
              variant="secondary"
              label={t('testnet.mintOnchain')}
              request={{ address: D.usdt, abi: faucetAbi, functionName: 'mint', args: [address, parseEther('1000')] }}
            />
          )}
        </span>
      ) : null}
    </div>
  );
}
